import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { ANT_PLUS_DEVICES } from "@/lib/antPlusDevices";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";

const CUSTOM_DEVICE_VALUE = "__not_in_ant_plus_list__";

export default function AntPlusDeviceCombobox({
  value,
  onChange,
  disabled,
  labels,
  testId = "ant-plus-device",
  required = false,
  dropdownSide = "bottom",
}) {
  const [open, setOpen] = useState(false);
  const [customDevice, setCustomDevice] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [search, setSearch] = useState("");

  const selectedDevice = ANT_PLUS_DEVICES.includes(value) ? value : "";
  const isCustom = customMode || Boolean(value && !selectedDevice);
  const buttonLabel = value || labels.placeholder;
  const filteredDevices = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) return ANT_PLUS_DEVICES;
    return ANT_PLUS_DEVICES.filter((device) => device.toLowerCase().includes(normalized));
  }, [search]);

  const customInputValue = useMemo(() => {
    if (customDevice) return customDevice;
    return value && !selectedDevice ? value : "";
  }, [customDevice, selectedDevice, value]);

  const chooseDevice = (nextValue) => {
    if (nextValue === CUSTOM_DEVICE_VALUE) {
      setCustomMode(true);
      setCustomDevice(isCustom ? value : "");
      onChange(isCustom ? value : "");
    } else {
      setCustomMode(false);
      setCustomDevice("");
      onChange(nextValue);
    }
    setOpen(false);
  };

  const updateCustomDevice = (nextValue) => {
    setCustomDevice(nextValue);
    onChange(nextValue.trim());
  };

  return (
    <div className="relative space-y-2">
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className={cn(
          "w-full justify-between rounded-lg font-normal",
          !value && "text-muted-foreground"
        )}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        data-testid={`${testId}-trigger`}
      >
        <span className="truncate">{buttonLabel}</span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open ? (
        <div
          className={cn(
            "absolute left-0 right-0 z-[70] overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-md",
            dropdownSide === "top" ? "bottom-[calc(100%+0.5rem)]" : "top-10"
          )}
          data-testid={`${testId}-dropdown`}
        >
          <Command shouldFilter={false}>
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder={labels.searchPlaceholder}
            />
            <CommandList
              className="h-72 max-h-[min(18rem,calc(100dvh-12rem))] overflow-y-auto overscroll-contain"
              onWheel={(event) => event.stopPropagation()}
              onTouchMove={(event) => event.stopPropagation()}
            >
              <CommandGroup>
                <CommandItem
                  value={CUSTOM_DEVICE_VALUE}
                  onSelect={chooseDevice}
                  data-testid={`${testId}-not-in-list`}
                >
                  <Check className={cn("h-4 w-4", isCustom ? "opacity-100" : "opacity-0")} />
                  <span>{labels.notInList}</span>
                </CommandItem>
                {filteredDevices.length === 0 ? (
                  <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                    {labels.noResults}
                  </div>
                ) : null}
                {filteredDevices.map((device) => (
                  <CommandItem
                    key={device}
                    value={device}
                    onSelect={chooseDevice}
                    data-testid={`${testId}-option`}
                  >
                    <Check
                      className={cn(
                        "h-4 w-4",
                        selectedDevice === device ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span>{device}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      ) : null}

      {isCustom || customDevice ? (
        <Input
          value={customInputValue}
          onChange={(event) => updateCustomDevice(event.target.value)}
          placeholder={labels.customPlaceholder}
          required={required}
          disabled={disabled}
          data-testid={`${testId}-custom`}
        />
      ) : null}
    </div>
  );
}
